import React, { useEffect, useState } from "react";
import docentesService from "../../../../services/docentesService";

function Paginacion({ pagina, totalPaginas, onCambiar }) {
  if (totalPaginas <= 1) return null;

  const pages = [];
  const delta = 2;
  const left  = Math.max(1, pagina - delta);
  const right = Math.min(totalPaginas, pagina + delta);

  if (left > 1) {
    pages.push(1);
    if (left > 2) pages.push("...");
  }
  for (let p = left; p <= right; p++) pages.push(p);
  if (right < totalPaginas) {
    if (right < totalPaginas - 1) pages.push("...");
    pages.push(totalPaginas);
  }

  return (
    <nav className="d-flex justify-content-center mt-3">
      <ul className="pagination pagination-sm mb-0">
        <li className={`page-item ${pagina === 1 ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onCambiar(pagina - 1)}>
            <i className="bi bi-chevron-left" />
          </button>
        </li>
        {pages.map((p, i) =>
          p === "..." ? (
            <li key={`e${i}`} className="page-item disabled">
              <span className="page-link">…</span>
            </li>
          ) : (
            <li key={p} className={`page-item ${p === pagina ? "active" : ""}`}>
              <button className="page-link" onClick={() => onCambiar(p)}>{p}</button>
            </li>
          )
        )}
        <li className={`page-item ${pagina === totalPaginas ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onCambiar(pagina + 1)}>
            <i className="bi bi-chevron-right" />
          </button>
        </li>
      </ul>
    </nav>
  );
}

// Modal de confirmación reutilizable dentro del componente
function ModalConfirmar({ datos, onConfirm, onCancel }) {
  if (!datos) return null;
  return (
    <div className="modal d-block" style={{ background: "rgba(0,0,0,.45)" }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
        <div className="modal-content">
          <div className="modal-header border-0 pb-0">
            <h6 className="modal-title fw-bold">
              <i className={`bi ${datos.icon} me-2 ${datos.iconClass}`} />
              {datos.titulo}
            </h6>
            <button type="button" className="btn-close" onClick={onCancel} />
          </div>
          <div className="modal-body pt-2">
            <p className={`text-muted small ${datos.lista?.length ? "mb-2" : "mb-0"}`}>{datos.mensaje}</p>
            {datos.lista?.length ? (
              <ul className="small mb-0 ps-3">
                {datos.lista.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            ) : null}
          </div>
          <div className="modal-footer border-0 pt-1 gap-2">
            <button className="btn btn-secondary btn-sm" onClick={onCancel}>
              Cancelar
            </button>
            <button className={`btn btn-sm ${datos.btnClass}`} onClick={onConfirm}>
              {datos.btnLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DocentesList({
  docentes = [],
  loading,
  reload,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
  canManageCampus,
  togglingCampus,
  onCampusToggle,
  onCampusReset,
  togglingActivo,
  onToggleActivo,
  busqueda = "",
  setBusqueda = () => {},
  pagina = 1,
  setPagina = () => {},
  totalFiltradas = 0,
  totalPaginas = 1,
  porPagina = 10,
}) {
  const offsetInicio = (pagina - 1) * porPagina;
  const [campusEstados, setCampusEstados] = useState({});
  const [confirmacion, setConfirmacion]   = useState(null); // { tipo, docente, ...modal }

  useEffect(() => {
    if (!docentes.length || !canManageCampus) return;
    let cancelado = false;
    (async () => {
      // Secuencial (no Promise.all) para no disparar una rafaga de N peticiones
      // simultaneas al backend, que en produccion provoca 404 intermitentes
      // por condicion de carrera en el proxy bajo trafico concurrente.
      for (const d of docentes) {
        if (cancelado) return;
        try {
          const res = await docentesService.campusEstado(d.id);
          if (!cancelado) setCampusEstados((prev) => ({ ...prev, [d.id]: res }));
        } catch { /* silencioso */ }
      }
    })();
    return () => { cancelado = true; };
  }, [docentes, canManageCampus]);

  const refreshEstado = async (docente_id) => {
    try {
      const res = await docentesService.campusEstado(docente_id);
      setCampusEstados((prev) => ({ ...prev, [docente_id]: res }));
    } catch { /* silencioso */ }
  };

  // Pedir confirmación antes de desactivar
  const pedirDesactivar = (docente) => {
    setConfirmacion({
      tipo: "desactivar",
      docente,
      titulo: "Desactivar acceso Campus",
      mensaje: `¿Desactivar el acceso Campus de ${docente.nombres} ${docente.apellidos}? El docente no podrá ingresar hasta que lo vuelvas a habilitar.`,
      icon: "bi-toggle-off",
      iconClass: "text-danger",
      btnClass: "btn-danger",
      btnLabel: "Desactivar",
    });
  };

  // Pedir confirmación antes de desactivar al docente para malla/cronograma
  // (distinto del acceso Campus). Se listan las materias asignadas AHORA,
  // en el propio diálogo de confirmación — antes se avisaba con un toast que
  // desaparecía a los pocos segundos y, si la lista era larga, no daba
  // tiempo a leerla ni a hacer nada con esa información antes de desactivar.
  const [cargandoMateriasFor, setCargandoMateriasFor] = useState(null);
  const pedirDesactivarDocente = async (docente) => {
    setCargandoMateriasFor(docente.id);
    let materias = [];
    try {
      const res = await docentesService.materiasQueDicta(docente.id);
      materias = Array.isArray(res?.materias) ? res.materias : [];
    } catch {
      /* si falla la consulta, igual se puede desactivar; solo no se lista */
    } finally {
      setCargandoMateriasFor(null);
    }

    // Un docente puede salir dos veces para la misma materia (preferente Y
    // calificado a la vez); se combinan en una sola línea.
    const porMateria = new Map();
    materias.forEach((m) => {
      const key = `${m.materia_id}-${m.curso_id}`;
      const previo = porMateria.get(key);
      porMateria.set(key, {
        materia_nombre: m.materia_nombre,
        curso_nombre: m.curso_nombre,
        es_preferente: Boolean(previo?.es_preferente || m.es_preferente),
      });
    });
    const materiasUnicas = Array.from(porMateria.values());

    const base = `¿Desactivar a ${docente.nombres} ${docente.apellidos}? Ya no se podrá asignar a materias nuevas en Malla Curricular ni en el Cronograma.`;
    setConfirmacion({
      tipo: "desactivarDocente",
      docente,
      titulo: "Desactivar docente",
      mensaje: materiasUnicas.length
        ? `${base} Actualmente dicta (no se le quita automáticamente):`
        : `${base} No tiene materias asignadas actualmente.`,
      lista: materiasUnicas.map(
        (m) => `${m.materia_nombre} (${m.curso_nombre})${m.es_preferente ? " — preferente" : ""}`
      ),
      icon: "bi-person-x",
      iconClass: "text-danger",
      btnClass: "btn-danger",
      btnLabel: "Desactivar",
    });
  };

  // Pedir confirmación antes de resetear
  const pedirReset = (docente) => {
    setConfirmacion({
      tipo: "reset",
      docente,
      titulo: "Resetear acceso Campus",
      mensaje: `Se generará un nuevo PIN de 6 dígitos para ${docente.nombres} ${docente.apellidos}. El PIN anterior quedará invalidado.`,
      icon: "bi-arrow-clockwise",
      iconClass: "text-warning",
      btnClass: "btn-warning",
      btnLabel: "Generar nuevo PIN",
    });
  };

  const handleConfirmar = async () => {
    const { tipo, docente } = confirmacion;
    setConfirmacion(null);

    if (tipo === "desactivar") {
      await onCampusToggle?.(docente, false);
      await refreshEstado(docente.id);
    } else if (tipo === "reset") {
      await onCampusReset?.(docente);
      await refreshEstado(docente.id);
    } else if (tipo === "desactivarDocente") {
      await onToggleActivo?.(docente);
    }
  };

  const handleToggleOn = async (docente) => {
    await onCampusToggle?.(docente, true);
    await refreshEstado(docente.id);
  };

  if (loading) {
    return (
      <div className="management-card text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="management-card docentes-table-card">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
          <div>
            <p className="management-label mb-1">Listado</p>
            <h5 className="mb-0">
              Docentes registrados
              {totalFiltradas > 0 && (
                <span className="text-muted fw-normal fs-6 ms-2">
                  ({totalFiltradas})
                </span>
              )}
            </h5>
          </div>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <div className="input-group input-group-sm" style={{ width: 220 }}>
              <span className="input-group-text">
                <i className="bi bi-search" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por nombre, cédula o email…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
              {busqueda && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setBusqueda("")}
                  title="Limpiar búsqueda"
                >
                  <i className="bi bi-x" />
                </button>
              )}
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={reload}
              disabled={loading}
            >
              <i className="bi bi-arrow-repeat me-2" />
              Actualizar
            </button>
          </div>
        </div>

        {docentes.length === 0 ? (
          <div className="text-center py-5 text-muted">
            {busqueda ? (
              <>
                <i className="bi bi-search fs-2 d-block mb-2" />
                <p className="mb-0">Sin resultados para <strong>"{busqueda}"</strong>.</p>
                <button
                  type="button"
                  className="btn btn-link btn-sm mt-1"
                  onClick={() => setBusqueda("")}
                >
                  Limpiar búsqueda
                </button>
              </>
            ) : (
              <>
                <i className="bi bi-person-badge docentes-empty-icon d-block mb-3" />
                <h5 className="fw-semibold mb-1">No hay docentes registrados.</h5>
                <p className="mb-0">Crea un docente o actualiza la lista.</p>
              </>
            )}
          </div>
        ) : (
          <>
        <div className="management-table-wrapper">
          <table className="table align-middle management-table docentes-table mb-0">
            <thead>
              <tr>
                <th style={{ width: "4%" }}>#</th>
                <th style={{ width: "18%" }}>Nombres</th>
                <th style={{ width: "18%" }}>Apellidos</th>
                <th style={{ width: "13%" }}>Documento</th>
                <th style={{ width: "18%" }}>Email</th>
                <th style={{ width: "8%" }}>Activo</th>
                {canManageCampus && <th style={{ width: "12%" }}>Campus</th>}
                <th style={{ width: "9%" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {docentes.map((docente, index) => {
                const campus    = campusEstados[docente.id];
                const isToggling = togglingCampus === docente.id;

                return (
                  <tr key={docente.id}>
                    <td>{offsetInicio + index + 1}</td>
                    <td className="fw-semibold">{docente.nombres}</td>
                    <td>{docente.apellidos}</td>
                    <td className="font-monospace text-muted">{docente.documento}</td>
                    <td>
                      {docente.email ? (
                        <a href={`mailto:${docente.email}`} className="text-decoration-none">
                          {docente.email}
                        </a>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      <div
                        className="form-check form-switch mb-0"
                        title={docente.activo ? "Desactivar docente" : "Activar docente"}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={!!docente.activo}
                          disabled={!onToggleActivo || togglingActivo === docente.id || cargandoMateriasFor === docente.id}
                          onChange={() =>
                            docente.activo ? pedirDesactivarDocente(docente) : onToggleActivo?.(docente)
                          }
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    </td>

                    {canManageCampus && (
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {/* Toggle on/off */}
                          <div
                            className="form-check form-switch mb-0"
                            title={campus?.activo ? "Desactivar acceso Campus" : "Habilitar acceso Campus"}
                          >
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              checked={!!campus?.activo}
                              disabled={isToggling || campus === undefined}
                              onChange={() =>
                                campus?.activo
                                  ? pedirDesactivar(docente)
                                  : handleToggleOn(docente)
                              }
                              style={{ cursor: "pointer" }}
                            />
                          </div>

                          {/* PIN pendiente */}
                          {campus?.activo && campus?.pin_pendiente && (
                            <span
                              className="badge bg-warning text-dark small"
                              title="PIN generado, pendiente de activar por el docente"
                            >
                              <i className="bi bi-key-fill" />
                            </span>
                          )}

                          {/* Activo y configurado */}
                          {campus?.activo && !campus?.primer_acceso && (
                            <span className="badge bg-success small" title="Cuenta activa">
                              <i className="bi bi-check2" />
                            </span>
                          )}

                          {/* Resetear PIN */}
                          {campus?.activo && (
                            <button
                              className="btn btn-sm btn-outline-secondary p-1"
                              title="Resetear acceso (generar nuevo PIN)"
                              disabled={isToggling}
                              onClick={() => pedirReset(docente)}
                            >
                              <i className="bi bi-arrow-clockwise" style={{ fontSize: 11 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}

                    <td>
                      <div className="docentes-table-actions">
                        {canEdit && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => onEdit?.(docente)}
                            title="Editar docente"
                          >
                            <i className="bi bi-pencil" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => onDelete?.(docente)}
                            title="Eliminar docente"
                          >
                            <i className="bi bi-trash" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

          {/* Paginación + contador */}
          <div className="d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2">
            <small className="text-muted">
              Mostrando {offsetInicio + 1}–{Math.min(offsetInicio + porPagina, totalFiltradas)} de {totalFiltradas} docente{totalFiltradas !== 1 ? "s" : ""}
            </small>
            <Paginacion
              pagina={pagina}
              totalPaginas={totalPaginas}
              onCambiar={setPagina}
            />
          </div>
          </>
        )}
      </div>

      <ModalConfirmar
        datos={confirmacion}
        onConfirm={handleConfirmar}
        onCancel={() => setConfirmacion(null)}
      />
    </>
  );
}
